"use client";

import { useT } from "@/lib/i18n/context";
import type { WShopLatestProductsContent } from "@/lib/api/cms-window-content";
import { ProductCard } from "@/components/ui/ProductCard/ProductCard";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import { wShopLatestProductsTranslations } from "./WShopLatestProducts.i18n";
import styles from "./WShopLatestProducts.module.css";

/**
 * WShopLatestProducts - okno "najnowsze produkty".
 *
 * Grid kafelkow produktow (reuse `<ProductCard />` z ui/), naglowek sekcji z
 * tytulem instancji okna + opcjonalny przycisk "zobacz wszystkie" gdy admin
 * ustawil `config[list_url]`. Fallback title z i18n gdy admin nie nazwal
 * instancji (`content.title` puste).
 *
 * Rendering identyczny z `WShopPromotionalProducts` - roznica wylacznie w danych
 * (backend sortuje po `add_date DESC` zamiast filtrowac po `promocja=1`). Kafelek
 * i grid sa te same (`<ProductCard />` obsluguje badge "new" jesli produkt ma
 * flage).
 *
 * Data source: `W_SHOP_LATEST_PRODUCTS_CONTROLLER::getApiContent()`.
 */
export function WShopLatestProducts({
  content,
  title,
}: WShopLatestProductsProps) {
  const t = useT(wShopLatestProductsTranslations);

  if (!content) return null;

  const headingText = title || content.title || t.title;
  const showAllHref = content.href ? extractInternalHref(content.href) : null;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{headingText}</h2>
        {content.href && (
          showAllHref !== null ? (
            <Link href={showAllHref} className={styles.showAllBtn}>
              {t.showAll}
            </Link>
          ) : (
            <a href={content.href} className={styles.showAllBtn}>
              {t.showAll}
            </a>
          )
        )}
      </div>

      {content.items.length === 0 ? (
        <div className={styles.empty}>{t.noProducts}</div>
      ) : (
        <ul className={styles.productList}>
          {content.items.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              showPrices={content.show_prices}
              currency={content.currency}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export type WShopLatestProductsProps = {
  content: WShopLatestProductsContent | null;
  title: string;
};
